import { Clock, Share2, Zap } from "lucide-react";

import { Badge } from "@/app/components/Badge/Badge";
import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";
import { formatRelativeTime } from "@/app/handlers/utilityFunctions";
import { useJob } from "@/app/services/create-resume/queries";

import Loader from "../../../components/Loader";
import { FitCheckResponse } from "../config/api";
import { BaselineItemDisplay } from "./BaselineItemDisplay";

interface FitCheckCardProps {
	fitCheck: FitCheckResponse;
	onViewDetails: () => void;
}

export const FitCheckCard = ({ fitCheck, onViewDetails }: FitCheckCardProps) => {
	const { data: jobData, isLoading: isJobLoading } = useJob(fitCheck.jobId);
	const job = jobData?.data;

	const baselineCheck = fitCheck.fitCheck.baselineCheck;
	const meetsCount = [
		baselineCheck.education,
		baselineCheck.experience,
		baselineCheck.skills,
		baselineCheck.certification,
	].filter((item) => item.status === "MEET").length;
	const isReadyToApply = meetsCount >= 3;

	// Get first strength or gap for each baseline item
	const getDescription = (item: { status: string; strengths?: string[]; gaps?: string[] }) => {
		if (item.status === "MEET" && item.strengths && item.strengths.length > 0) {
			return item.strengths[0];
		}
		if (item.gaps && item.gaps.length > 0) {
			return item.gaps[0];
		}
		return item.status === "MEET" ? "Meets requirements" : "Missing requirements";
	};

	if (isJobLoading) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6">
				<div className="flex items-center justify-center py-8">
					<Loader />
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6">
			{/* Status badge and share */}
			<div className="flex items-center justify-between mb-2">
				<Badge
					className={cn(
						"text-xs font-medium rounded-xs flex items-center gap-1 border-0",
						isReadyToApply
							? "bg-green-100 text-green-700 hover:bg-green-100"
							: "bg-blue-100 text-blue-600 hover:bg-yellow-100",
					)}
				>
					<Zap className="w-3 h-3" />
					{isReadyToApply ? "Ready to Apply" : "Prepare First"}
				</Badge>
				<button className="text-gray-400 hover:text-gray-600">
					<Share2 className="w-4 h-4" />
				</button>
			</div>

			{/* Job info */}
			<div className="flex items-start gap-3 mb-5">
				<img
					src={job.company.logo_url}
					alt={job.companyName}
					className="w-12 h-12 rounded object-cover bg-gray-50"
					onError={(e) => (e.currentTarget.src = "/images/co-placeholder.png")}
				/>
				<div>
					<h3 className="font-bold! font-plus-jakarta! text-base text-gray-900">
						{fitCheck.fitCheck.roleTitle || job?.jobTitle || "Unknown Position"}
					</h3>
					<p className="text-sm font-plus-jakarta text-gray-700">
						{fitCheck.fitCheck.company || job?.companyName || "Unknown Company"}
					</p>
				</div>
			</div>

			{/* Baseline checks - combining skills & certification */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
				<BaselineItemDisplay
					label="Education"
					description={getDescription(baselineCheck.education)}
					status={baselineCheck.education.status}
				/>
				<BaselineItemDisplay
					label="Experience"
					description={getDescription(baselineCheck.experience)}
					status={baselineCheck.experience.status}
				/>
				<BaselineItemDisplay
					label="Skills & Certification"
					description={
						baselineCheck.skills.status === "MEET" && baselineCheck.certification.status === "MEET"
							? "Meets the required skills & certification"
							: baselineCheck.skills.gaps?.[0] ||
								baselineCheck.certification.gaps?.[0] ||
								"Missing skills or certification"
					}
					status={
						baselineCheck.skills.status === "MEET" && baselineCheck.certification.status === "MEET" ? "MEET" : "MISSING"
					}
				/>
			</div>

			{/* Footer */}
			<div className="flex items-center justify-between pt-4 border-t border-gray-100">
				<div className="flex items-center gap-1 text-xs text-gray-400">
					<Clock className="w-3 h-3" />
					<span>{formatRelativeTime(fitCheck.createdAt)}</span>
				</div>
				<div className="flex items-center gap-2">
					<Button size="sm" variant="default" onClick={onViewDetails}>
						View Details
					</Button>
					{isReadyToApply ? (
						<Button size="sm">Apply Now</Button>
					) : (
						<Button size="sm" variant="secondary">
							FitCheck Again
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default FitCheckCard;
